import React from 'react';
import { useTranslation } from 'react-i18next';

import type { Asset } from '../../assets/types/Asset';
import type { CalendarEvent } from '../../calendar/types/Calendar';
import type { Contact } from '../../contacts/types/Contact';
import type { StructuredConsultationField } from '../consultationUtils';
import type { Consultation, ConsultationTemplate } from '../types/Consultation';
import {
  exportConsultationToJson,
  exportConsultationToPdf,
  type ConsultationExportInput,
} from '../utils/consultationExport';
import styles from './ConsultationExportMenu.module.css';

export type ConsultationExportMenuProps = {
  consultation: Consultation;
  title: string;
  status: string;
  summary: string;
  notes: string;
  asset?: Asset | null;
  contact?: Contact | null;
  template?: ConsultationTemplate | null;
  event?: CalendarEvent | null;
  fields: StructuredConsultationField[];
  fieldValues: Record<string, string>;
  disabled?: boolean;
  onExported?: (format: 'json' | 'pdf') => void;
  onError?: (message: string) => void;
};

export const ConsultationExportMenu: React.FC<ConsultationExportMenuProps> = ({
  consultation,
  title,
  status,
  summary,
  notes,
  asset,
  contact,
  template,
  event,
  fields,
  fieldValues,
  disabled = false,
  onExported,
  onError,
}) => {
  const { t } = useTranslation(['consultations', 'common']);
  const [open, setOpen] = React.useState(false);
  const menuRef = React.useRef<HTMLDivElement>(null);

  React.useEffect(() => {
    if (!open) {
      return undefined;
    }

    const handlePointerDown = (eventValue: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(eventValue.target as Node)) {
        setOpen(false);
      }
    };

    document.addEventListener('mousedown', handlePointerDown);
    return () => document.removeEventListener('mousedown', handlePointerDown);
  }, [open]);

  const buildExportInput = React.useCallback((): ConsultationExportInput => ({
    consultation,
    title,
    status,
    summary,
    notes,
    asset,
    contact,
    template,
    event,
    fields,
    fieldValues,
    labels: {
      consultation: t('consultation', { defaultValue: 'Consultation' }),
      status: t('status', { defaultValue: 'Status' }),
      summary: t('summary', { defaultValue: 'Summary' }),
      notes: t('notes', { defaultValue: 'Notes' }),
      asset: t('asset', { ns: 'assets', defaultValue: 'Asset' }),
      contact: t('contact', { defaultValue: 'Contact' }),
      template: t('template', { defaultValue: 'Template' }),
      linkedEvent: t('linkedEvent', { defaultValue: 'Linked event' }),
      structuredFields: t('structuredConsultationFields', { defaultValue: 'Structured fields' }),
      exportedAt: t('exportedAt', { defaultValue: 'Exported at' }),
      generalSection: t('generalSection', { defaultValue: 'General' }),
    },
  }), [asset, consultation, contact, event, fieldValues, fields, notes, status, summary, t, template, title]);

  const handleExport = async (format: 'json' | 'pdf') => {
    setOpen(false);

    try {
      const input = buildExportInput();
      if (format === 'json') {
        exportConsultationToJson(input);
      } else {
        await exportConsultationToPdf(input);
      }

      onExported?.(format);
    } catch (exportError) {
      onError?.(exportError instanceof Error
        ? exportError.message
        : t('consultationExportFailed', { defaultValue: 'Failed to export consultation.' }));
    }
  };

  return (
    <div className={styles.menu} ref={menuRef}>
      <button
        type="button"
        className="btn btn-secondary"
        disabled={disabled}
        aria-haspopup="menu"
        aria-expanded={open}
        onClick={() => setOpen((current) => !current)}
      >
        {t('export', { defaultValue: 'Export' })} ▾
      </button>
      {open ? (
        <div className={styles.menuList} role="menu">
          <button
            type="button"
            className={styles.menuItem}
            role="menuitem"
            onClick={() => handleExport('json')}
          >
            {t('exportToJson', { defaultValue: 'Export to JSON' })}
          </button>
          <button
            type="button"
            className={styles.menuItem}
            role="menuitem"
            onClick={() => handleExport('pdf')}
          >
            {t('exportToPdf', { defaultValue: 'Export to PDF' })}
          </button>
        </div>
      ) : null}
    </div>
  );
};
